import type { Page, Subsection } from '../../types';
import type { FetchFormResponse } from '../../../service/types';
import { getRoleLevelUrl, getSkillContent } from './contentBlocks';
import gdadSkillsMappingJson from './data/skills-mapping.json';

type GdadSkill = {
  id: string;
  name: string;
  description?: string;
};

type GdadRoleSkill = {
  skill_id: string;
  expected_level: string;
  order: number;
};

type GdadSkillsMapping = {
  self_assessment_levels: string[];
  skills: GdadSkill[];
  role_level_skills_map: Record<string, GdadRoleSkill[]>;
};

const gdadSkillsMapping = gdadSkillsMappingJson as GdadSkillsMapping;
const gdadSkillById: Record<string, GdadSkill> = Object.fromEntries(
  gdadSkillsMapping.skills.map((skill) => [skill.id, skill]),
);

const skillLevelOptions = [
  {
    option: 'None / Not applicable',
    value: '0',
  },
  {
    option: 'Working towards this level',
    value: '1',
  },
  {
    option: 'Working at this level',
    value: '2',
  },
  {
    option: 'Working above this level',
    value: '3',
  },
];

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const expectedLevelToIndex = (expectedLevel: string): number => {
  const idx = gdadSkillsMapping.self_assessment_levels.findIndex(
    (level) => String(level).toLowerCase() === String(expectedLevel).toLowerCase(),
  );
  return idx >= 0 ? idx + 1 : 0;
};

const buildLevelIndicator = (expectedLevel: string): string => {
  const levelIndex = expectedLevelToIndex(expectedLevel);
  if (levelIndex <= 0) {
    return `${expectedLevel} [ ][ ][ ][ ]`;
  }

  const boxes = Array.from({ length: 4 }, (_, index) => (index < levelIndex ? '[X]' : '[ ]')).join(
    '',
  );
  return `${expectedLevel} (${levelIndex}/4) ${boxes}`;
};

export const buildGdadSkillPages = (
  subsection: Subsection,
  fetchFormResponse: FetchFormResponse,
): Page[] => {
  const roleField = subsection.dynamicPages?.roleField || 'role';
  const roleLevelField = subsection.dynamicPages?.roleLevelField || 'role-level';
  const roleFamilyField = `${roleField}-family`;
  const role = String(fetchFormResponse?.answers?.[roleField] || '').trim();
  const roleLevel = String(fetchFormResponse?.answers?.[roleLevelField] || '').trim();
  const roleFamily = String(fetchFormResponse?.answers?.[roleFamilyField] || '').trim();

  if (!role || !roleLevel) {
    return [];
  }

  const mapKey = `${role}||${roleLevel}`;
  const mappedSkills = gdadSkillsMapping.role_level_skills_map[mapKey] || [];

  return mappedSkills
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((mappedSkill, index) => {
      const skill = gdadSkillById[mappedSkill.skill_id];
      const skillName = skill?.name || mappedSkill.skill_id;
      const roleUrl = getRoleLevelUrl(role, roleLevel) || '';
      const safeRoleUrl = escapeHtml(roleUrl);
      const safeRoleLevel = escapeHtml(roleLevel);
      const safeSkillName = escapeHtml(skillName);
      const safeSkillDescription = escapeHtml(String(skill?.description || '').trim());
      const safeLevelIndicator = escapeHtml(buildLevelIndicator(mappedSkill.expected_level));
      const skillContent = getSkillContent(role, roleLevel, mappedSkill.skill_id);
      const skillLevelDescription = `Expected capability for this role and level is ${mappedSkill.expected_level}.`;
      const skillUrl = skillContent?.url || '';
      const skillLevelMoreInformation =
        '<p><strong>Working towards this level</strong>: You have the level of experience to be able to deliver tasks in this skill, but may require help from time to time to successfully complete them.</p>' +
        '<p><strong>Working at this level</strong>: You can perform all the actions associated with this skill without assistance and each time they are required.</p>' +
        '<p><strong>Working above this level</strong>: Your expertise is recognised in this area. You can provide guidance, troubleshoot and answer questions related to the field where the skill is used.</p>';
      const exampleQuestion = {
        questionType: 'textarea' as const,
        title: 'Example(s) that support this',
        name: `skill-example-${mappedSkill.skill_id}`,
        textRows: 20,
        characterCount: 5000,
        rules: {
          required: true,
          maxLength: 5000,
        },
      };

      return {
        pageType: 'question',
        title: skillName,
        customTitle: roleLevel,
        name: `skill-${index + 1}-${mappedSkill.skill_id}`,
        hideSubTitle: true,
        navigateToSummaryOnComplete: true,
        description: [
          safeRoleUrl
            ? `<p class="govuk-body"><a class="govuk-link" href="${safeRoleUrl}" target="_blank" rel="noopener noreferrer">${safeRoleUrl}</a></p>`
            : '',
          safeRoleUrl ? `<p class="govuk-body"></p>` : '',
          skillContent ? '' : `<strong>${safeSkillName}</strong>`,
          skillContent ? '' : safeSkillDescription,
          skillContent ? '' : '',
          skillContent ? '' : skillLevelDescription,
          skillContent ? '' : '',
          skillContent ? '' : `Level: ${safeLevelIndicator}`,
        ]
          .filter((line, idx, arr) => {
            if (line !== '') {
              return true;
            }
            return arr[idx - 1] !== '';
          })
          .join('\n'),
        gdadSkillDefinition: skillContent
          ? {
              skillId: mappedSkill.skill_id,
              skillName: skillContent.skill || skillName,
              skillUrl,
              level: skillContent.level || mappedSkill.expected_level,
              bullets: Array.isArray(skillContent.bullets) ? skillContent.bullets : [],
              hideYouCanPrefix: roleFamily === 'Cyber Security',
            }
          : undefined,
        questions: [
          {
            questionType: 'select',
            title: 'Select your status:',
            name: `skill-level-${mappedSkill.skill_id}`,
            detailsHeading: 'More information',
            detailsContent: skillLevelMoreInformation,
            hintOption: 'Select an option...',
            options: [
              skillLevelOptions[0],
              {
                ...skillLevelOptions[1],
                questions: [exampleQuestion],
              },
              {
                ...skillLevelOptions[2],
                questions: [exampleQuestion],
              },
              {
                ...skillLevelOptions[3],
                questions: [exampleQuestion],
              },
            ],
            rules: {
              required: true,
            },
          },
        ],
      };
    });
};
